import { FinancialDirection } from './financial-report';

/**
 * The view model for the shared "Key figures" strip (company-profile-key-figures). Skins build it with their own
 * presenters (for example the Tax profile's tax-key-figures.ts); the strip only lays it out.
 */

/** One period of a figure's trend, oldest first. value is null when nothing was filed for that period. */
export interface KeyFigurePoint {
  /** Period end, YYYY-MM-DD. */
  period: string;
  /** Short period label for text alternatives, e.g. "2025" or "Dec 2025". */
  label: string;
  value: number | null;
  display: string;
}

export interface KeyFigureChange {
  direction: Exclude<FinancialDirection, null>;
  /** e.g. "18%" or "1.2 pts"; empty when only the direction is meaningful. */
  amount: string;
  /** Read in full by screen readers, e.g. "Up 18% on the prior period". */
  text: string;
}

export interface KeyFigureFlag {
  tone: 'caution' | 'notable';
  text: string;
}

export interface KeyFigure {
  id: string;
  label: string;
  /** Headline value, compact (e.g. "£2.8m", "5.4%"), or "Not filed" when missing. */
  value: string;
  /** The exact value, e.g. "£2,800,000", for the title and text alternative. */
  exact: string;
  missing: boolean;
  /** Unit or qualifier shown after the value, e.g. "of revenue" or "kWh". */
  unit?: string;
  /** One short secondary line, e.g. "48 employees" or "Electricity 12.0m kWh · Gas 11.2m kWh". */
  detail?: string;
  /** One line on why the figure matters for the relief. */
  why: string;
  /** The financial report tab that shows this figure, or null when the report does not include it. */
  reportTab: string | null;
  /** A different action for figures the report does not show, e.g. opening a recorded history; emitted as (figureAction). */
  action?: { id: string; label: string };
  /** Set only when this figure comes from a different period from the strip's reference period. */
  periodLabel?: string;
  change: KeyFigureChange | null;
  /** Up to five periods, oldest first. A trend is drawn only when at least two have values. */
  trend: KeyFigurePoint[];
  trendKind: 'line' | 'bars';
  flag?: KeyFigureFlag;
}

/** One limit of a size test, e.g. turnover against £15m. */
export interface KeyFigureLimit {
  label: string;
  value: string;
  limit: string;
  /** value / limit, for the meter; null when not filed. */
  ratio: number | null;
  within: boolean | null;
  reportTab: string;
}

/** A threshold test, e.g. the small-company audit exemption limits. */
export interface KeyFigureCheck {
  eyebrow: string;
  /** e.g. "Within the small-company limits" */
  result: string;
  /** e.g. "Meets 2 of 3 for the year ended 31 Dec 2025" */
  summary: string;
  /** true within, false above, null when there are too few filed figures to say. */
  outcome: boolean | null;
  /** The prior period's outcome, e.g. "Prior period: also within the limits". */
  prior?: string;
  limits: KeyFigureLimit[];
  /** One line naming the limits used and where they come from. */
  basis: string;
  flag?: KeyFigureFlag;
}

export interface KeyFigureAdviser {
  role: string;
  name: string | null;
  /** Where the name comes from, e.g. "Named in the accounts" or "Holds an outstanding charge". */
  source: string;
  /** e.g. "Accounts to 31 Dec 2025" or "Registered 2 Sep 2022". */
  period?: string;
}

export interface KeyFigureStrip {
  /** Reference period end, YYYY-MM-DD, or '' when no period is filed. */
  period: string;
  /** e.g. "Year ended 31 Dec 2025" */
  periodLabel: string;
  /** The accounts type filed for the reference period, e.g. "Medium (Standalone)". */
  accountType?: string;
  /** A short notice about the reference period, e.g. that no profit and loss account was filed. */
  notice?: string;
  figures: KeyFigure[];
  check?: KeyFigureCheck;
  advisers?: KeyFigureAdviser[];
  /** The report tab "View full financial report" opens. */
  reportTab: string;
}

/** Compact money for headline figures: £2.8m, £944k, -£33k, £512. */
export function compactMoney(value: number): string {
  const sign = value < 0 ? '-' : '';
  return `${sign}£${compactNumber(Math.abs(value))}`;
}

/** Compact numbers: 24.0m, 944k, 1.2bn, 512. Millions keep one decimal place so trends read evenly. */
export function compactNumber(value: number): string {
  const size = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const format = (number: number, digits: number) =>
    new Intl.NumberFormat('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(number);
  if (size >= 1e9) return `${sign}${format(size / 1e9, 1)}bn`;
  if (size >= 1e6) return `${sign}${format(size / 1e6, 1)}m`;
  if (size >= 1e4) return `${sign}${format(size / 1e3, 0)}k`;
  return `${sign}${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(size)}`;
}

export function exactMoney(value: number): string {
  const formatted = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(Math.abs(value));
  return `${value < 0 ? '-' : ''}£${formatted}`;
}

export function exactNumber(value: number, digits = 0): string {
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: digits }).format(value);
}

export function percent(value: number, digits = 1): string {
  return `${new Intl.NumberFormat('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value)}%`;
}
